import { Campaing, Devices, Keyword, DeviceServer, DeviceStatus } from 'common';
import { Sentence } from 'common';
import { RecordSession } from 'common/index';
import { Locale } from '../stores/locale';
import { User } from '../stores/user';
import { ENVIRONMENT } from '../constants';

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  isJSON?: boolean;
  isTextResponse?: boolean;
  isBlob?: boolean;
  headers?: {
    [headerName: string]: string;
  };
  body?: any;
  signal?: AbortSignal;
}

const API_PATH = ENVIRONMENT.api_path || null;
const DEVICES_PATH = ENVIRONMENT.devices_path || null;

// Todo: Refactor
export const DEVICES_SERVERS: DeviceServer[] = [
  {
    address: DEVICES_PATH,
    ip: '192.168.2.2:5000',
    up: false,
  },
];

export default class API {
  private readonly locale: Locale.State;
  private readonly user: User.State;

  constructor(locale: Locale.State, user: User.State) {
    this.locale = locale;
    this.user = user;
  }

  async fetch(path: string, options: FetchOptions = {}): Promise<any> {
    const { method, headers, body, isJSON, isTextResponse, isBlob } =
      Object.assign(
        {
          isJSON: true,
          isTextResponse: false,
          isBlob: false,
        },
        options
      );

    const finalHeaders = Object.assign(
      isJSON
        ? {
            'Content-Type': 'application/json; charset=utf-8',
          }
        : {},
      headers
    );

    // const { user } = this;
    // if (path.startsWith(location.origin) && !user.account && user.userId) {
    //   finalHeaders['Authorization'] =
    //     'Basic ' + btoa(user.userId + ':' + user.authToken);
    // }

    const response = await fetch(path, {
      method: method || 'GET',
      headers: finalHeaders,
      // credentials: 'same-origin',
      body: body
        ? body instanceof Blob
          ? body
          : JSON.stringify(body)
        : undefined,
    });
    if (response.status >= 400) {
      if (response.statusText.includes('save_clip_error')) {
        throw new Error(response.statusText);
      }
      throw new Error(await response.text());
    }

    if (isTextResponse) {
      return response.text();
    }

    if (isBlob) {
      return response.blob();
    }

    return isJSON ? response.json() : response.text();
  }

  forLocale(locale: string) {
    return new API(locale, this.user);
  }

  async fetchRandomSentences(count = 1): Promise<Sentence[]> {
    const s: Campaing[] = await this.fetch(API_PATH + '/campaign');
    const sentences: Sentence[] = [];
    s.forEach(s => {
      s.keywords.forEach(kw => {
        sentences.push({
          createdAt: s.createdAt,
          text: kw.localizedKeywords[0].text,
          audioLength: kw.audioLength,
          campaignId: s.id,
          id: kw.id.toString(),
          locale: kw.localizedKeywords[0].locale,
          repsCount: s.repCount,
        });
      });
    });
    return sentences;
  }

  async pigDevicesServer(server: string): Promise<string> {
    return this.fetch(server + '/ping', { isTextResponse: true });
  }

  async getDevicesStatus(): Promise<DeviceStatus[]> {
    return new Promise<DeviceStatus[]>((resolve, reject) => {
      this.fetch(DEVICES_PATH + '/status')
        .then(res =>
          resolve(
            res.devices_status.map((r: DeviceStatus) => ({
              ...r,
              serverIP: '192.168.2.2:5000',
            }))
          )
        )
        .catch(() => resolve(null));
      setTimeout(() => {
        resolve(null);
      }, 1500);
    });
  }

  async deleteDevice(mac: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.fetch(DEVICES_PATH + '/delete/' + mac)
        .then(res => resolve())
        .catch(() => reject());
      setTimeout(() => {
        reject();
      }, 1500);
    });
  }

  async fetchLangSentences(lang: string): Promise<Sentence[]> {
    const defaultCampaing: Campaing = await this.getDefaultCampaing();
    const sentences: Sentence[] = [];

    // Check default
    if (defaultCampaing) {
      defaultCampaing.keywords.forEach(keyword => {
        sentences.push({
          createdAt: defaultCampaing.createdAt,
          text: keyword.localizedKeywords.find(
            lk => lk.locale.toLowerCase() == lang.toLowerCase()
          ).text,
          audioLength: keyword.audioLength,
          campaignId: defaultCampaing.id,
          id: keyword.id.toString(),
          locale: lang,
          repsCount: defaultCampaing.repCount,
        });
      });
    } else {
      const campaings = await this.getCampaings();
      campaings.forEach((c: Campaing) => {
        c.keywords.forEach(keyword => {
          sentences.push({
            createdAt: c.createdAt,
            text: keyword.localizedKeywords.find(
              lk => lk.locale.toLowerCase() == lang.toLowerCase()
            ).text,
            audioLength: keyword.audioLength,
            campaignId: c.id,
            id: keyword.id.toString(),
            locale: lang,
            repsCount: c.repCount,
          });
        });
      });
    }

    return sentences;
  }

  async getLoginFile(): Promise<any> {
    return this.fetch('/dist/login.json');
  }

  async fetchDevices(): Promise<Devices> {
    return new Promise<Devices>((resolve, reject) => {
      this.fetch(DEVICES_PATH + '/list_devices')
        .then(res => resolve(res))
        .catch(() => resolve(null));
      setTimeout(() => {
        resolve(null);
      }, 1500);
    });
  }

  async startRecording(
    macs: string[],
    length?: number
  ): Promise<{ audio_files: { dev_mac: string; file: string }[] }> {
    const body = {
      device_mac: macs,
      audio_len: length,
    };
    return this.fetch(DEVICES_PATH + '/start_recording', {
      body: body,
      method: 'POST',
    });
  }

  async getRecord(audioName: string) {
    return this.fetch(DEVICES_PATH + '/get_audio_file/' + audioName, {
      isBlob: true,
    });
  }

  uploadClip(
    blob: Blob,
    keywordId: string,
    microphoneId?: string,
    locale?: 'EN' | 'ES',
    fromDemo?: boolean
  ) {
    const form = new FormData();
    form.append('keywordId', keywordId);
    form.append('sessionId', this.user.userId);
    form.append('microphoneId', microphoneId);
    form.append('locale', locale);
    form.append('file', blob);

    return fetch(API_PATH + '/upload', {
      method: 'POST',
      body: form,
    });
  }

  getCampaings(): Promise<Campaing[]> {
    return this.fetch(API_PATH + '/campaign');
  }

  getBackendVersion(): Promise<{ message: string }> {
    return this.fetch(API_PATH + '/app_version');
  }

  getDefaultCampaing(): Promise<Campaing> {
    return this.fetch(API_PATH + '/campaign/default');
  }

  setCampaingDefault(campaingId: number) {
    return this.fetch(API_PATH + '/campaign/set_default/' + campaingId, {
      method: 'PUT',
    });
  }

  getCampaing(id: string): Promise<Campaing> {
    return this.fetch(API_PATH + '/campaign/' + id);
  }

  getKeywords(): Promise<Keyword[]> {
    return this.fetch(API_PATH + '/keyword');
  }

  addKeyword(keyword: Keyword): Promise<any> {
    return this.fetch(API_PATH + '/keyword', {
      method: 'POST',
      body: keyword,
    });
  }

  editKeyword(keyword: Keyword): Promise<any> {
    return this.fetch(API_PATH + '/keyword', {
      method: 'PUT',
      body: keyword,
    });
  }

  deleteKeyword(id: number): Promise<any> {
    return this.fetch(API_PATH + '/keyword/' + id, {
      method: 'DELETE',
    });
  }

  deleteCampaing(id: string): Promise<string> {
    return this.fetch(API_PATH + '/campaign/' + id, {
      method: 'DELETE',
      isTextResponse: true,
    });
  }

  updateCampaing(campaing: Campaing) {
    return this.fetch(API_PATH + '/campaign/', {
      method: 'PUT',
      body: campaing,
      isTextResponse: true,
    });
  }

  addCampaing(campaing: Campaing) {
    return this.fetch(API_PATH + '/campaign/', {
      method: 'POST',
      body: campaing,
      isTextResponse: true,
    });
  }

  saveSession(data: RecordSession) {
    // data: UserSession
    return this.fetch(API_PATH + '/session', {
      method: 'POST',
      body: {
        age: data.age,
        gender: data.gender,
        motherTongue: data.motherTongue,
        nationality: data.nationality,
        region: data.region,
        echo: data.echo,
        micType: data.micType,
        comments: data.comments,
      },
    });
  }

  fetchLocaleMessages(locale: string): Promise<string> {
    return this.fetch(`/locales/${locale}/messages.ftl`, { isJSON: false });
  }
}
